import { DateField, TypeaheadField } from '~/common/components';
import { useI18n, Trans } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import { CUSTOMER_LEVEL, IOptionItem } from '@wyz/types';
import {
  Accordion,
  Card,
  CenteredSpinner,
  Col,
  FormContainer,
  Row,
  Title,
  notify,
} from '@wyz/ui';
import { DateUtils } from '@wyz/utils';
import * as React from 'react';

import { useForm } from 'react-hook-form';
import { useManageQuoteServicePricingPolicies } from '~/purchase/api';
import { useQuoteServicePricingPolicyForm } from '~/purchase/components';
import { QuoteServicePricingPolicyHelpers } from '~/purchase/helpers';
import {
  PRICING_STATUS_ENUM,
  quoteDefaultPricingApplicationStepFormSchema,
} from '~/purchase/schemas';
import { usePricingPolicyErrorTranslation } from '~/common/hooks';
import {
  IGlobalQuoteDefaultServicePricingFormData,
  IPricingStatus,
  IQuoteDefaultServicePricingApplicationStepForm,
} from '~/purchase/types';
import { useManageCustomers } from '~/common/api';

type CommonFormProps = {
  globalFormData: IGlobalQuoteDefaultServicePricingFormData;
  policyGroupId?: number;
};

type ApplicationStepFormProps = CommonFormProps & {
  onSubmit: (
    data: IQuoteDefaultServicePricingApplicationStepForm,
    status: IPricingStatus,
  ) => void;
};

const ApplicationStepForm: React.FC<ApplicationStepFormProps> = ({
  onSubmit,
  globalFormData,
  policyGroupId,
}) => {
  const { t } = useI18n();
  const { resolveErrorKey } = usePricingPolicyErrorTranslation();
  const manageServicePricingPolicies = useManageQuoteServicePricingPolicies({
    checkOverlap: { enabled: true },
  });
  const methods = useForm<IQuoteDefaultServicePricingApplicationStepForm>({
    defaultValues: globalFormData.APPLICATION,
    resolver: zodResolver(quoteDefaultPricingApplicationStepFormSchema),
  });
  const pricingPolicyForm = useQuoteServicePricingPolicyForm();
  const manageCustomers = useManageCustomers({
    autocomplete: { enabled: true },
  });

  return (
    <FormContainer
      methods={methods}
      submitHandler={(formData, event) => {
        manageServicePricingPolicies.checkOverlap.mutate(
          {
            customerIds: [...formData.customers],
            startDate: DateUtils.formatTimestamp(formData.startDate, {
              customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
            }),
            endDate: formData.endDate
              ? DateUtils.formatTimestamp(formData.endDate, {
                  customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
                })
              : null,
            default: true,
            policyGroupId: policyGroupId,
          },
          {
            onError: (error) => {
              notify.error(<Trans i18nKey={resolveErrorKey(error.message)} />, {
                id: 'toast-error-quote-default-service-pricing-policy-overlap-check',
              });
            },
            onSuccess: () => {
              onSubmit(
                formData,
                ((event?.nativeEvent as SubmitEvent)?.submitter?.getAttribute(
                  'data-status',
                ) as IPricingStatus) ?? PRICING_STATUS_ENUM.DRAFT,
              );
            },
          },
        );
      }}
      formConfiguration={{ mode: pricingPolicyForm.mode }}
      id={QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX.APPLICATION.toString()}
    >
      <Card className='mb-4'>
        {manageServicePricingPolicies.checkOverlap.isLoading && (
          <div
            className='position-absolute h-100 w-100'
            style={{ backgroundColor: 'rgba(0,0,0,.1)', zIndex: 5 }}
          >
            <div className='center-v-h'>
              <CenteredSpinner />
            </div>
          </div>
        )}
        <Card.Header>
          <Card.Title>
            <Title heading='xl' weight='bold'>
              <Trans
                i18nKey='app.service.pricing_policy.form.application.title'
                values={{
                  name: globalFormData.MOUNTING?.name,
                }}
              />
            </Title>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col>
              <DateField
                name='startDate'
                label={<Trans i18nKey='app.common.start_date' />}
                placeholderText='dd-MM-yyyy'
                dateFormat={DateUtils.DATE_FORMAT.DMY}
                required
                showTimeSelect={false}
                translations={{
                  applyButton: t('app.common.button.apply'),
                  resetButton: t('app.common.button.reset'),
                }}
              />
            </Col>
            <Col>
              <DateField
                name='endDate'
                label={<Trans i18nKey='app.common.end_date' />}
                dateFormat={DateUtils.DATE_FORMAT.DMY}
                placeholderText='dd-MM-yyyy'
                showTimeSelect={false}
                translations={{
                  applyButton: t('app.common.button.apply'),
                  resetButton: t('app.common.button.reset'),
                }}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Accordion.Container flush defaultActiveKey='0'>
            <Accordion.Item eventKey='0'>
              <Accordion.Header>
                <Title heading='xl' weight='bolder'>
                  <Trans i18nKey='app.pricing_policy.redistribution.form.application.customers.title' />
                </Title>
              </Accordion.Header>
              <Accordion.Body>
                <TypeaheadField<true, IOptionItem<string, string>>
                  required={false}
                  isClearable
                  minCharacter={3}
                  maxShown={10}
                  minCharacterMessage={
                    <Trans
                      i18nKey='app.common.autocomplete.min_character_message'
                      values={{
                        minCharacter: 3,
                      }}
                    />
                  }
                  name={'customersAutocomplete'}
                  label={
                    <Trans i18nKey='app.pricing_policy.redistribution.form.application.customers.title' />
                  }
                  fetcher={(query, options) =>
                    manageCustomers.autocomplete(query, {
                      ...options,
                      authorizedLevels: [CUSTOMER_LEVEL.N2, CUSTOMER_LEVEL.N1],
                    })
                  }
                  onSelect={(selected) => {
                    methods.setValue(
                      'customers',
                      selected.map((item) => item.value),
                    );
                  }}
                  isMulti
                  wrapItems
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion.Container>
        </Card.Body>
      </Card>
    </FormContainer>
  );
};

export default ApplicationStepForm;
